import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, MessageSquare, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { sendContactMessage } from '../lib/api';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      await sendContactMessage(formData);
      toast.success('Message sent! We\'ll respond within 24 hours.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast.error('Failed to send message. Please try again or email us directly.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>Contact Us | SquareCount Roof Measurement Software</title>
        <meta name="description" content="Get in touch with the SquareCount team. We're here to help roofing contractors get accurate roof measurements faster and cheaper." />
        <link rel="canonical" href="https://squarecount.io/contact" />
        <meta property="og:title" content="Contact SquareCount" />
        <meta property="og:description" content="Get in touch with the SquareCount team for roof measurement software support." />
        <meta property="og:url" content="https://squarecount.io/contact" />
      </Helmet>
      <Navbar />

      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl mb-6">
            Contact SquareCount Support
          </h1>
          <p className="text-xl text-slate-300">
            Have questions? We're here to help. Get in touch and we'll respond within 24 hours.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6">
            <Mail className="w-8 h-8 text-orange-500 mb-3" />
            <h3 className="font-heading font-bold text-lg text-white mb-2">Email</h3>
            <p className="text-slate-300 mb-2">support@squarecount.io</p>
            <p className="text-sm text-slate-500">For general inquiries and support</p>
          </div>

          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6">
            <MessageSquare className="w-8 h-8 text-orange-500 mb-3" />
            <h3 className="font-heading font-bold text-lg text-white mb-2">Live Chat</h3>
            <p className="text-slate-300 mb-2">Available in dashboard</p>
            <p className="text-sm text-slate-500">Real-time support for users</p>
          </div>

          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6">
            <Clock className="w-8 h-8 text-orange-500 mb-3" />
            <h3 className="font-heading font-bold text-lg text-white mb-2">Response Time</h3>
            <p className="text-slate-300 mb-2">Within 24 hours</p>
            <p className="text-sm text-slate-500">Usually much faster!</p>
          </div>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 rounded-lg max-w-2xl mx-auto p-6 sm:p-8">
          <h2 className="font-heading font-bold text-2xl text-white mb-6">Send Us a Message</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-slate-300">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                className="mt-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="mt-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>

            <div>
              <Label htmlFor="subject" className="text-slate-300">Subject</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                required
                className="mt-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>

            <div>
              <Label htmlFor="message" className="text-slate-300">Message</Label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                required
                rows={6}
                className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 placeholder:text-slate-500"
              />
            </div>

            <Button
              type="submit"
              disabled={sending}
              className="w-full bg-orange-500 hover:bg-orange-400 text-white font-heading font-bold"
            >
              {sending ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        </div>
      </section>
      <Footer />
    </div>
  );
}
